import * as React from 'react'
import * as deepEqual from 'react-fast-compare'
import { DomainDto } from '@halo/dtos/domain.dto'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import {
    BarChartOutlined,
    CopyOutlined,
    EditOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Modal, notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import moment from 'moment-timezone'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { DomainService } from '@halo/services/domain.service'
import { Container } from 'typescript-ioc/es5'
import { legacyValidateFields } from '@halo/_utils/antd'
import {
    DEFAULT_DOMAIN_INDUSTRY_ID,
    DEFAULT_DRAWER_FORM_ITEM_LAYOUT,
    FEAT_SDK_EVENT_ONLY_DOMAINS,
} from '@halo/constants'
import { Link } from 'react-router-dom'
import * as randomstring from 'randomstring'
import { prepareWhitelistDomains } from '@halo/_utils/domain'
import './style/domain-data-form.scss'
import ColorPickerPopover from '@halo/components/color-picker-popover/color-picker-popover'
import { escapeRegExp } from '@halo/_utils/regexp'
import { Flags } from '@pushly/aquarium-utils/lib/enums/flags'
import TestDomainOverlay from '@halo/components/test-domain-overlay/test-domain-overlay'
import GenerateTestDomainModal from '@halo/components/generate-test-domain-modal/generate-test-domain-modal'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { ApiEnumService } from '@halo/services/api-enum.service'
import { Industry } from '@pushly/models/lib/structs/industry'

interface IDomainDetailsEditor extends FormComponentProps {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
    industries: Industry[]
}

interface IDDEState {
    color: string
    prefix: string
    name: string
}

export const DomainDetailsEditor = Form.create<IDomainDetailsEditor>()(
    class extends React.Component<IDomainDetailsEditor, IDDEState> {
        public state: IDDEState = {
            color: this.props.domain?.display_meta?.overlay_config?.color ?? '#ffbf00',
            prefix: this.props.domain?.display_meta?.prefix ?? 'TEST',
            name: '',
        }

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService

        public constructor(props: IDomainDetailsEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const { industries } = this.props
            const currentValues = this.getCurrentValues()

            const timezones = moment.tz.names()
            const suggestedTimezone = moment.tz.guess()

            const name = this.domain.name
            let displayName = this.domain.display_name
            const industryId = this.domain.industry_id ?? DEFAULT_DOMAIN_INDUSTRY_ID
            const prefix = this.domain?.display_meta?.prefix

            if (this.domain?.prod_domain_id !== null && !!prefix) {
                const rgx = new RegExp(`^\\(${escapeRegExp(prefix)}\\)\\s`, 'i')
                displayName = displayName.replace(rgx, '')
            }

            return (
                <Drawer
                    className={classnames('ddf-details-editor')}
                    title="Update Details"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}>
                        <Well title="Domain Information" mode="ghost" hideFooter={true}>
                            <Form.Item label="Display Name" className={classnames('ddf-details-editor-name')}>
                                {this.domain.prod_domain_id ? (
                                    <Input.Group compact={true} size="small">
                                        {this.props.form.getFieldDecorator('prefix', {
                                            initialValue: prefix || 'TEST',
                                        })(
                                            <Select
                                                size="small"
                                                onSelect={(value: string) => {
                                                    this.setState({ prefix: value })
                                                    this.props.form.setFieldsValue({ prefix: value })
                                                }}
                                            >
                                                <Select.Option value="TEST">Test</Select.Option>
                                                <Select.Option value="DEV">Dev</Select.Option>
                                                <Select.Option value="QA">QA</Select.Option>
                                            </Select>,
                                        )}
                                        <Input value={displayName} disabled={true} />
                                    </Input.Group>
                                ) : (
                                    this.props.form.getFieldDecorator('displayName', {
                                        initialValue: displayName,
                                        rules: [
                                            {
                                                required: true,
                                                message: 'Display name is required.',
                                            },
                                        ],
                                    })(<Input size="small" />)
                                )}
                            </Form.Item>

                            <Form.Item label="Domain Name" className={classnames('ddf-details-editor-name')}>
                                {this.props.form.getFieldDecorator('name', {
                                    initialValue: name,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'Name is required.',
                                        },
                                    ],
                                })(<Input size="small" />)}
                            </Form.Item>

                            <Form.Item label="Industry" className={classnames('ddf-details-editor-industry')}>
                                {this.props.form.getFieldDecorator('industryId', {
                                    initialValue: industryId,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'Industry is required.',
                                        },
                                    ],
                                })(
                                    <Select
                                        size="small"
                                        showSearch={true}
                                        placeholder="Select an Industry"
                                        optionFilterProp="title"
                                    >
                                        {industries.map((industry: Industry) => (
                                            <Select.Option
                                                key={industry.id}
                                                value={industry.id}
                                                title={`${industry.name}`}
                                            >
                                                {industry.name}
                                            </Select.Option>
                                        ))}
                                    </Select>,
                                )}
                            </Form.Item>

                            {this.domain.prod_domain_id && (
                                <Form.Item label="Icon Overlay Color" className="generate-test-confirm-label">
                                    <div className="generate-test-confirm-group">
                                        <div className="color-selections">
                                            {this.props.form.getFieldDecorator('overlayColor', {
                                                initialValue: this.domain.display_meta?.overlayConfig?.color,
                                            })(
                                                <ColorPickerPopover
                                                    defaultColor={this.state.color}
                                                    color={this.state.color}
                                                    onChange={(color) => this.setState({ color })}
                                                />,
                                            )}

                                            <TestDomainOverlay
                                                label={this.state.prefix}
                                                color={this.state.color}
                                                icon={this.domain.default_icon_url}
                                            />
                                        </div>
                                    </div>
                                </Form.Item>
                            )}
                        </Well>

                        <Well title="Platform Configuration" mode="ghost" hideFooter={true}>
                            <Form.Item label="Time Zone" className={classnames('ddf-details-editor-timezone')}>
                                {this.props.form.getFieldDecorator('timezone', {
                                    initialValue: this.domain.timezone || suggestedTimezone,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'Time Zone must be selected',
                                        },
                                    ],
                                })(
                                    <Select size="small" showSearch={true} placeholder="Select a time zone">
                                        {timezones.map((tz: string) => (
                                            <Select.Option key={tz} value={tz}>
                                                {tz}
                                            </Select.Option>
                                        ))}
                                    </Select>,
                                )}
                                <div className={classnames('form-layout-row-sub')}>
                                    <InfoCircleOutlined />
                                    <span>
                                        Modifying the time zone on a domain that already has acquisition or delivery
                                        stats may affect historical reporting. Historical reporting will continue to be
                                        reported in the time zone in which the domain was set to when the data was
                                        received and any new data coming in will be attributed to the correct time zone.
                                    </span>
                                </div>
                            </Form.Item>

                            <Form.Item
                                label="SDK Allowed Domains"
                                className={classnames('ddf-details-editor-whitelist')}
                            >
                                {this.props.form.getFieldDecorator('whitelistDomains', {
                                    initialValue: this.domain.whitelist_domains || [],
                                })(
                                    <Select
                                        size="small"
                                        mode="tags"
                                        className="domains-allowed stacked-tags"
                                        dropdownClassName="disabled whitelist-domains-dropdown"
                                        placeholder="https://www.pushly.com, https://subdomain.pushly.com"
                                        getPopupContainer={this.appService.getAppContainer}
                                        tokenSeparators={[',', ';', ' ', '\n']}
                                        notFoundContent=""
                                    />,
                                )}
                            </Form.Item>

                            {this.domainHasEventOnlyDomainsFlag && (
                                <Form.Item
                                    label="SDK Event Only Domains"
                                    className={classnames('ddf-details-editor-sdk-ev-only-domains')}
                                >
                                    {this.props.form.getFieldDecorator('sdkEventOnlyDomains', {
                                        initialValue: this.domain.sdk_event_only_domains,
                                    })(
                                        <Select
                                            size="small"
                                            mode="tags"
                                            className="domains-allowed stacked-tags"
                                            dropdownClassName="disabled whitelist-domains-dropdown"
                                            placeholder="https://www.pushly.com, https://subdomain.pushly.com"
                                            getPopupContainer={this.appService.getAppContainer}
                                            tokenSeparators={[',', ';', ' ', '\n']}
                                            notFoundContent=""
                                        />,
                                    )}
                                </Form.Item>
                            )}
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get domain(): DomainDto {
            return this.props.domain
        }

        protected get domainHasEventOnlyDomainsFlag(): boolean {
            return this.props.domain.flags.includes(FEAT_SDK_EVENT_ONLY_DOMAINS) ?? false
        }

        protected getCurrentValues(): Partial<DomainDto> {
            const formValues = this.props.form.getFieldsValue()
            let values: Partial<DomainDto> = {}

            let display_meta = { ...(this.domain.display_meta ?? {}) }

            // display_meta update, should contain a prefix regardless of update
            if (
                formValues.overlayColor !== this.domain.display_meta?.overlay_config?.color &&
                formValues.overlayColor !== undefined
            ) {
                display_meta = {
                    ...display_meta,
                    overlay_config: {
                        color: formValues.overlayColor,
                    },
                }
            }

            if (!!formValues.prefix) {
                display_meta = {
                    ...display_meta,
                    prefix: formValues.prefix,
                }
            }

            if (!deepEqual(display_meta, this.domain.display_meta ?? {})) {
                values.display_meta = display_meta
            }

            if (formValues.displayName !== this.domain.display_name) {
                values.display_name = formValues.displayName
            }

            if (formValues.name !== this.domain.name) {
                values.name = formValues.name
            }

            if (formValues.timezone !== this.domain.timezone) {
                values.timezone = formValues.timezone
            }

            if (!deepEqual(formValues.whitelistDomains, this.domain.whitelist_domains || [])) {
                values.whitelist_domains = prepareWhitelistDomains(formValues.whitelistDomains)
            }

            if (this.domainHasEventOnlyDomainsFlag) {
                if (!deepEqual(formValues.sdkEventOnlyDomains, this.domain.sdk_event_only_domains)) {
                    values.sdk_event_only_domains = formValues.sdkEventOnlyDomains
                }
            }

            if (formValues.industryId !== this.domain.industry_id) {
                values.industry_id = formValues.industryId
            }

            return values
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const values = this.getCurrentValues()

                let { ok, data } = await this.domainService.patch(this.domain.id, values, {
                    showLoadingScreen: true,
                    cancellationKey: `patch-domain-${this.domain.id}`,
                })

                if (ok) {
                    this.props.onDomainUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IAccountDetailsWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDWState {
    showDrawer: boolean
    drawerKey?: string
    showTestForm: boolean
    industries: Industry[]
}

export class DomainDetailsWell extends React.Component<IAccountDetailsWell, IDWState> {
    public state: IDWState = {
        showDrawer: false,
        showTestForm: false,
        industries: [],
    }

    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService
    protected apiEnumService: ApiEnumService

    public constructor(props: IAccountDetailsWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.apiEnumService = Container.get(ApiEnumService)
    }

    public async componentDidMount() {
        await this.fetchAllIndustries()
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Details"
                actions={
                    this.appState.abilityStore.can(
                        AbilityAction.UPDATE,
                        asCaslSubject(SubjectEntity.DOMAIN, {
                            ...this.props.domain,
                            statusId: this.props.domain?.status_id,
                        }),
                    ) && this.buildActions()
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderOrgWell()}
                            {this.renderInformationWell()}
                            {this.renderConfigurationWell()}

                            <DomainDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                                industries={this.state.industries}
                            />

                            <GenerateTestDomainModal
                                domain={this.domain}
                                loading={this.props.loading}
                                onOk={this.handleTestDomainCreate}
                                visible={this.state.showTestForm}
                                onCancel={this.hideNewTestForm}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected buildActions(): React.ReactNode {
        const domain = this.props.domain
        const domainFlags = domain?.flags ?? []
        const actions = []

        const addActionDividerIfNecessary = (nodes: any[]) => {
            if (nodes.length > 0) {
                nodes.push(<span key="spacer1" className="actions-spacer" />)
            }
        }

        if (domain) {
            const demoIdRgx = /DEMO_ID_(?<demo_id>[\d]+)/i
            const flagMatch = demoIdRgx.exec(domainFlags.join(','))
            const demoDomainId = flagMatch?.groups?.demo_id

            const isDemoDomain = domainFlags.includes(Flags.domain.DEMO.key) && !!demoDomainId
            const isProdDomain = domain.prod_domain_id === null || domain.prod_domain_id === undefined

            // Test domain generation should not be available to internal DEMO domains
            if (isProdDomain && !isDemoDomain) {
                actions.push(
                    <span key="activate">
                        <Button size="small" onClick={this.showNewTestForm} disabled={this.props.loading}>
                            <CopyOutlined />
                            <span>Generate Test Domain</span>
                        </Button>
                    </span>,
                )
            }

            if (isDemoDomain) {
                addActionDividerIfNecessary(actions)

                actions.push(
                    <span key="reset-stats">
                        <Button
                            size="small"
                            onClick={() => this.resetStats(demoDomainId)}
                            disabled={this.props.loading}
                        >
                            <BarChartOutlined />
                            <span>Reset Stats</span>
                        </Button>
                    </span>,
                )

                actions.push(
                    <span key="reset-full">
                        <Button size="small" onClick={() => this.resetFull(demoDomainId)} disabled={this.props.loading}>
                            <GlobalOutlined />
                            <span>Reset Domain</span>
                        </Button>
                    </span>,
                )
            }
        }

        addActionDividerIfNecessary(actions)

        actions.push(
            <Button key="edit" size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                <EditOutlined />
                <span>Edit</span>
            </Button>,
        )

        return actions
    }

    protected renderOrgWell() {
        return (
            <Well title="Organization" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Org Name</div>
                    <div className={classnames('form-layout-row-value')}>
                        <Link to={`/organizations/${this.domain.account_id}`}>{this.domain.account_name}</Link>
                    </div>
                </div>
            </Well>
        )
    }

    protected renderInformationWell() {
        return (
            <Well title="Domain Information" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Display Name</div>
                    <div className={classnames('form-layout-row-value')}>{this.domain.display_name}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Integration Type</div>
                    <div className={classnames('form-layout-row-value')}>Native</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Domain Name</div>
                    <div className={classnames('form-layout-row-value')}>
                        <a href={`https://${this.domain.name}`} target="_blank">
                            {this.domain.name}
                        </a>
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>SDK Key</div>
                    <div className={classnames('form-layout-row-value')}>{this.domain.domain_key ?? ''}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Industry</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.domain.industry?.name ?? 'Uncategorized'}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Created</div>
                    <div className={classnames('form-layout-row-value')}>
                        {moment(this.domain.created_at).format('LLL')}
                        <span className="lowlight"> by </span>
                        {this.domain.created_by_user_name}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Last Updated</div>
                    <div className={classnames('form-layout-row-value')}>
                        {moment(this.domain.updated_at).format('LLL')}
                        <span className="lowlight"> by </span>
                        {this.domain.updated_by_user_name}
                    </div>
                </div>
            </Well>
        )
    }

    protected renderConfigurationWell() {
        return (
            <Well title="Platform Configuration" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Time Zone</div>
                    <div className={classnames('form-layout-row-value')}>
                        {!this.domain.timezone ? (
                            'None selected'
                        ) : (
                            <span>
                                {this.domain.timezone} | {moment().tz(this.domain.timezone).format('z | Z')}
                            </span>
                        )}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Currency</div>
                    <div className={classnames('form-layout-row-value')}>
                        {!this.domain.currency ? (
                            'None selected'
                        ) : (
                            <span>
                                {this.domain.currency.name} | {this.domain.currency_code}
                            </span>
                        )}
                    </div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>Domain currency cannot be changed once set.</span>
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>SDK Allowed Domains</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.domain.whitelist_domains?.join(', ') ?? '*'}
                    </div>
                    <div className={classnames('form-layout-row-sub')}>
                        <WarningOutlined />
                        <span>
                            Wildcard <b>*</b> entries are not supported by Apple's APNs and will cause package failures
                            for Safari integrations.
                        </span>
                    </div>
                </div>

                {this.domainHasEventOnlyDomainsFlag && (
                    <div className={classnames('form-layout-row')}>
                        <div className={classnames('form-layout-row-label')}>SDK Event Only Domains</div>
                        <div className={classnames('form-layout-row-value')}>
                            {this.domain.sdk_event_only_domains.join(', ') || 'N/A'}
                        </div>
                        <div className={classnames('form-layout-row-sub')}>
                            <WarningOutlined />
                            <span>
                                All domains must also be included in the SDK Allowed Domains. Wildcard <b>*</b> entries
                                are not supported for event only domains.
                            </span>
                        </div>
                    </div>
                )}
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected get domainHasEventOnlyDomainsFlag(): boolean {
        return this.props.domain.flags.includes(FEAT_SDK_EVENT_ONLY_DOMAINS) ?? false
    }

    protected showNewTestForm = () => {
        this.setState({ showTestForm: true })
    }

    protected hideNewTestForm = () => {
        this.setState({ showTestForm: false })
    }

    protected resetStats = async (demoId: number | string) => {
        Modal.confirm({
            title: 'Reset Demo Stats',
            content: <div>Are you sure you wish to reset all stats associated with this demo domain?</div>,
            onOk: async () => {
                await this.domainService.resetDemoStats(demoId, {
                    showLoadingScreen: true,
                    cancellationKey: `demoStatsReset.${demoId}`,
                })
            },
        })
    }

    protected resetFull = async (demoId: number | string) => {
        Modal.confirm({
            title: 'Reset Demo Domain',
            content: (
                <div>
                    Are you sure you wish to fully reset this demo domain? This action will reset all associated
                    entities and cannot be reversed
                </div>
            ),
            onOk: async () => {
                await this.domainService.resetDemoDomain(demoId, {
                    showLoadingScreen: true,
                    cancellationKey: `demoReset.${demoId}`,
                })
            },
        })
    }

    protected buildDomainUrl(domain: DomainDto): string {
        return `/domains/${domain.id}`
    }

    protected jumpToDomain(domain: DomainDto): void {
        this.appService.route(this.buildDomainUrl(domain))
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }

    protected handleTestDomainCreate = (domain: DomainDto) => {
        this.jumpToDomain(domain)
    }

    protected async fetchAllIndustries(): Promise<void> {
        const { ok, data: industries } = await this.apiEnumService.fetchAllIndustries({
            query: {
                pagination: 0,
            },
            showLoadingScreen: false,
        })

        if (ok) {
            this.setState({ industries })
        }
    }
}
